import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { Candidate, Constellation, Query } from "@/api/types";
import { formatDate, formatPercent, viewLabel } from "@/lib/format";
import { Evidence, Readout } from "@/site/evidence";
import { CertificateLink, LogSightingForm, NameSharkForm } from "@/site/forms";
import { PhotoTips, RarityMark, SectionHead, count, to, useStats } from "@/site/parts";
import { Plate, sharkAspect } from "@/site/plate";

// ------------------------------------------------------------------ shared

/** Spot pairs, or why there are none: a shark on file only from another view is linked by
 * MiewID alone, its spots never compared with this photograph's. */
export function pairsLabel(c: Candidate): string {
  return c.evidence === "other_view" ? "MiewID only" : `${c.n_pairs} pairs`;
}

/** The side read from the photo: spots are only compared with photos of the same view. */
export function viewRow(query: Query): [string, string] {
  const v = query.view;
  if (!v) return ["View", "—"];
  const label = viewLabel(v.label);
  return ["View", label ? `${label} · ${formatPercent(v.confidence)}` : "Unclear · all views"];
}

function ConfBar({ value, top = false }: { value: number; top?: boolean }) {
  return (
    <span className="conf-bar" data-top={top} aria-hidden="true">
      <span style={{ width: `${Math.min(Math.max(value, 0), 1) * 100}%` }} />
    </span>
  );
}

function CandidateTable({ candidates, start = 1 }: { candidates: Candidate[]; start?: number }) {
  return (
    <div className="scroll-x">
      <table className="table">
        <thead>
          <tr>
            <th>Rank</th>
            <th>Specimen</th>
            <th className="hide-sm">Rarity</th>
            <th style={{ width: "22%" }}>P(same)</th>
            <th className="r">Pairs</th>
            <th className="r hide-sm">Score</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((c, i) => (
            <tr key={c.shark.id}>
              <td className="mono">{String(i + start).padStart(2, "0")}</td>
              <td>
                <Link to={to.shark(c.shark.id)} className="link-u">
                  {c.shark.display_name}
                </Link>
                <span className="label" style={{ display: "block" }}>
                  {c.shark.id}
                </span>
              </td>
              <td className="hide-sm">
                <RarityMark rarity={c.shark.rarity} />
              </td>
              <td>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <ConfBar value={c.confidence} />
                  <span className="mono" style={{ fontSize: 12 }}>
                    {formatPercent(c.confidence)}
                  </span>
                </span>
              </td>
              <td className="r mono">{c.evidence === "other_view" ? "—" : c.n_pairs}</td>
              <td className="r mono hide-sm">{c.score.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CalibrationNote() {
  const metrics = useStats()?.benchmark?.metrics ?? [];
  if (metrics.length === 0) return null;
  return (
    <aside className="in-wrap in-section" aria-labelledby="calibration-note">
      <div className="note">
        <p id="calibration-note" className="label" style={{ color: "var(--ink)" }}>
          Reliability note
        </p>
        <p style={{ marginTop: 6 }}>
          Confidence is calibrated on a held-out benchmark of known re-sightings.{" "}
          {metrics
            .slice(-2)
            .map((m) => `${m.label}: ${m.value}${m.note ? ` (${m.note})` : ""}.`)
            .join(" ")}
        </p>
      </div>
    </aside>
  );
}

function ResultHead({
  kicker,
  title,
  children,
  aside,
}: {
  kicker: React.ReactNode;
  title: React.ReactNode;
  children?: React.ReactNode;
  aside?: React.ReactNode;
}) {
  return (
    <section className="in-wrap in-grid" style={{ paddingTop: 48, rowGap: 32 }}>
      <div style={{ gridColumn: "span 8" }} className="anim-in">
        <p className="label label-signal">{kicker}</p>
        <h1 className="display-2" style={{ marginTop: 14 }}>
          {title}
        </h1>
        {children}
      </div>
      {aside && (
        <div style={{ gridColumn: "9 / span 4" }} className="anim-in">
          {aside}
        </div>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ processing

export function ProcessingResult({
  imageUrl,
  constellation,
  startedAt,
}: {
  imageUrl: string;
  constellation: Constellation | null;
  startedAt: string | null;
}) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    return () => clearInterval(timer);
  }, []);
  const steps = ["Segment body", "Extract spots", "Match constellations", "Calibrate"];
  return (
    <section className="in-wrap in-grid" style={{ paddingTop: 48, rowGap: 32 }} aria-busy="true">
      <div style={{ gridColumn: "span 8" }}>
        <div className="fig-head">
          <span className="label">A · Query · your photograph</span>
          <span className="label">
            {constellation ? `${constellation.spots.length} spots` : "Reading"}
          </span>
        </div>
        {constellation ? (
          <Plate
            imageUrl={imageUrl}
            constellation={constellation}
            aspect={sharkAspect(constellation, 1.2, 2.4)}
            scanning
            label="Your photograph, spots being read"
          />
        ) : (
          <div className="plate">
            <img src={imageUrl} alt="Your photograph, being read" style={{ width: "100%" }} />
            <div className="scan" aria-hidden="true" />
          </div>
        )}
      </div>
      <div style={{ gridColumn: "9 / span 4", display: "grid", gap: 24, alignContent: "start" }}>
        <p className="label label-signal">Result · pending</p>
        <h1 className="display-2">Reading the spots.</h1>
        <div role="status" aria-live="polite" className="readout" style={{ gap: 12 }}>
          <span className="label">Elapsed</span>
          <span className="readout-value" style={{ fontSize: 72 }}>
            {String(Math.floor(elapsed / 60)).padStart(2, "0")}:
            {String(elapsed % 60).padStart(2, "0")}
          </span>
          <div className="bar-indeterminate" />
        </div>
        <ol className="tips">
          {steps.map((s, i) => (
            <li key={s}>
              <span className="label">{String(i + 1).padStart(2, "0")}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
        <p className="muted" style={{ fontSize: 14 }}>
          {startedAt ? "Usually under a minute. " : ""}This page updates itself; you can leave it
          open.
        </p>
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ match

export function MatchResult({ query }: { query: Query }) {
  const best = query.candidates[0];
  if (!best) {
    return (
      <div className="in-wrap in-section">
        <p role="alert" className="alert">
          The server reported a match but sent no candidate. Retry later.
        </p>
      </div>
    );
  }
  const shark = best.shark;
  const runner = query.candidates[1];
  const lastSeen = formatDate(shark.last_seen);
  return (
    <>
      <ResultHead
        kicker="Result · positive identification"
        title={shark.display_name}
        aside={
          <Readout
            candidates={query.candidates}
            label="P(same individual)"
            rows={[
              viewRow(query),
              ["Reference view", viewLabel(best.image.view) ?? "—"],
              ["Matched pairs", String(best.n_pairs)],
              [
                "Spots · query / reference",
                `${query.constellation?.spots.length ?? "—"} / ${best.image.constellation.spots.length}`,
              ],
              ["Match score", best.score.toFixed(1)],
              [
                "Runner-up",
                runner ? `${formatPercent(runner.confidence)} · ${runner.shark.id}` : "—",
              ],
            ]}
          />
        }
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px 22px",
            marginTop: 22,
            alignItems: "center",
          }}
        >
          <Link to={to.shark(shark.id)} className="mono link-u" style={{ fontSize: 15 }}>
            {shark.id}
          </Link>
          <RarityMark rarity={shark.rarity} large />
          <span className="label">ENC {shark.encounters}</span>
          {shark.regions[0] && <span className="label">{shark.regions.join(" · ")}</span>}
        </div>
        <p className="lede" style={{ marginTop: 22 }}>
          {best.n_pairs} spots in your photograph pair with {shark.id}&rsquo;s reference
          constellation. Seen {shark.encounters === 1 ? "once" : `${shark.encounters} times`} before
          {lastSeen ? `, last on ${lastSeen}` : ""}.
        </p>
      </ResultHead>
      <section className="in-wrap in-section" aria-labelledby="evidence">
        <SectionHead index="01" title="Evidence · spot correspondence" id="evidence">
          Hover a ring, a bar or a row: the same pair lights on every figure.
        </SectionHead>
        <Evidence query={query} candidate={best} />
      </section>
      <section className="in-wrap in-section" aria-labelledby="log">
        <SectionHead index="02" title="Log this sighting" id="log">
          Confirming adds your photograph to {shark.id}&rsquo;s record and issues a numbered
          certificate.
        </SectionHead>
        <div className="in-grid" style={{ rowGap: 32 }}>
          <div style={{ gridColumn: "span 5" }}>
            {query.certificate_id ? (
              <CertificateLink
                id={query.certificate_id}
                message="This sighting is already logged."
              />
            ) : (
              <LogSightingForm
                queryId={query.id}
                sharkId={shark.id}
                sharkName={shark.display_name}
              />
            )}
          </div>
          {query.candidates.length > 1 && (
            <div style={{ gridColumn: "7 / span 6" }}>
              <p className="label" style={{ marginBottom: 12 }}>
                Also considered
              </p>
              <CandidateTable candidates={query.candidates.slice(1)} start={2} />
            </div>
          )}
        </div>
      </section>
      <CalibrationNote />
    </>
  );
}

// ------------------------------------------------------------------ uncertain

function CandidateEntry({
  query,
  candidate,
  index,
}: {
  query: Query;
  candidate: Candidate;
  index: number;
}) {
  const [open, setOpen] = useState(index === 0);
  const [logging, setLogging] = useState(false);
  const shark = candidate.shark;
  const panel = `cand-${index}`;
  return (
    <li
      style={{
        borderTop: `1px solid ${index === 0 ? "var(--ink)" : "var(--rule)"}`,
        padding: "18px 0",
      }}
    >
      <div className="in-grid" style={{ rowGap: 14, alignItems: "center" }}>
        <span className="mono" style={{ gridColumn: "span 1", fontSize: 13 }}>
          {String(index + 1).padStart(2, "0")}
        </span>
        <div style={{ gridColumn: "span 4" }}>
          <Link to={to.shark(shark.id)} className="h-3 link-u">
            {shark.display_name}
          </Link>
          <div style={{ display: "flex", gap: 16, marginTop: 6, flexWrap: "wrap" }}>
            <span className="label" style={{ color: "var(--ink)" }}>
              {shark.id}
            </span>
            <RarityMark rarity={shark.rarity} />
          </div>
        </div>
        <div style={{ gridColumn: "span 3", display: "flex", alignItems: "center", gap: 12 }}>
          <ConfBar value={candidate.confidence} top={index === 0} />
          <span
            className="num"
            style={{
              fontSize: 28,
              fontWeight: 300,
              letterSpacing: "-0.03em",
              minWidth: "2.6em",
              textAlign: "right",
            }}
          >
            {formatPercent(candidate.confidence)}
          </span>
        </div>
        <span className="label" style={{ gridColumn: "span 1" }}>
          {pairsLabel(candidate)}
        </span>
        <div
          style={{
            gridColumn: "span 3",
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className="btn btn-ghost"
            aria-expanded={open}
            aria-controls={panel}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Hide evidence" : "Evidence"}{" "}
            <span className="btn-arrow">{open ? "−" : "+"}</span>
          </button>
          {!query.certificate_id && !logging && (
            <button type="button" className="btn" onClick={() => setLogging(true)}>
              This one <span className="btn-arrow">→</span>
            </button>
          )}
        </div>
      </div>
      {logging && (
        <div className="in-grid" style={{ paddingTop: 20 }}>
          <div style={{ gridColumn: "2 / span 5" }}>
            <LogSightingForm queryId={query.id} sharkId={shark.id} sharkName={shark.display_name} />
          </div>
        </div>
      )}
      {open && (
        <div id={panel} style={{ paddingTop: 28 }}>
          <Evidence query={query} candidate={candidate} />
        </div>
      )}
    </li>
  );
}

export function UncertainResult({ query }: { query: Query }) {
  const n = query.candidates.length;
  return (
    <>
      <ResultHead
        kicker="Result · inconclusive"
        title={`${n} candidate${n === 1 ? "" : "s"}. None clears the threshold.`}
        aside={
          <Readout
            candidates={query.candidates}
            label="Best P(same individual)"
            rows={[
              viewRow(query),
              ["Query spots", String(query.constellation?.spots.length ?? "—")],
              ["Best pairs", String(query.candidates[0]?.n_pairs ?? "—")],
              ["Naming", "Not offered"],
            ]}
          />
        }
      >
        <p className="lede" style={{ marginTop: 22 }}>
          {query.message ?? "Some spots line up, but not enough to be sure."} Judge the evidence
          yourself: if one of these is your shark, log it. Naming is only offered when a shark is
          confidently new; a near miss could be any of these at a different angle.
        </p>
        {query.certificate_id && (
          <div style={{ marginTop: 24 }}>
            <CertificateLink
              id={query.certificate_id}
              message="A sighting from this photograph is logged."
            />
          </div>
        )}
      </ResultHead>
      <section className="in-wrap in-section" aria-labelledby="candidates">
        <SectionHead index="01" title="Candidates" id="candidates">
          Ranked by calibrated probability. Open a candidate&rsquo;s evidence to compare spot by
          spot.
        </SectionHead>
        <ol
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            borderBottom: "1px solid var(--rule)",
          }}
        >
          {query.candidates.map((c, i) => (
            <CandidateEntry key={c.shark.id} query={query} candidate={c} index={i} />
          ))}
        </ol>
      </section>
      <CalibrationNote />
    </>
  );
}

// ------------------------------------------------------------------ new

export function NewResult({ query }: { query: Query }) {
  const stats = useStats();
  const c = query.constellation;
  const best = query.candidates[0];
  return (
    <>
      <ResultHead
        kicker="Result · no match in the registry"
        title={
          <>
            Not in the registry. <span className="signal">A new individual.</span>
          </>
        }
      >
        <div
          style={{
            display: "flex",
            gap: 20,
            marginTop: 22,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <RarityMark rarity="legendary" large />
          <span className="label">Discovery · first record</span>
        </div>
      </ResultHead>
      <section className="in-wrap in-grid" style={{ paddingTop: 48, rowGap: 40 }}>
        <figure style={{ gridColumn: "span 7", margin: 0 }}>
          <div className="fig-head">
            <span className="label">A · Query · your photograph</span>
            <span className="label">{c ? `${c.spots.length} spots` : "No fingerprint"}</span>
          </div>
          {c ? (
            <Plate
              imageUrl={query.image_url}
              constellation={c}
              aspect={sharkAspect(c, 1.1, 2.4)}
              label={`Your photograph with its ${c.spots.length} spots plotted`}
            />
          ) : (
            <img src={query.image_url} alt="Your photograph" style={{ width: "100%" }} />
          )}
          <figcaption className="fig-cap">
            <span className="label">Every detected spot plotted · no pair clears chance</span>
          </figcaption>
        </figure>
        <div style={{ gridColumn: "9 / span 4", display: "grid", gap: 32, alignContent: "start" }}>
          <dl className="kv">
            {[
              ["Catalogue no.", "Assigned on entry"],
              viewRow(query),
              ["Spots", String(c?.spots.length ?? "—")],
              ["Compared against", `${count(stats?.sharks)} individuals`],
              ["Best P(same)", best ? `${formatPercent(best.confidence)} · ${best.shark.id}` : "—"],
            ].map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
          {query.certificate_id ? (
            <CertificateLink
              id={query.certificate_id}
              message="This shark has been named and entered into the registry."
            />
          ) : query.can_name ? (
            <NameSharkForm queryId={query.id} />
          ) : (
            <p className="note">Naming is not available for this photograph.</p>
          )}
        </div>
      </section>
      {query.candidates.length > 0 && (
        <section className="in-wrap in-section" aria-labelledby="nearest">
          <SectionHead index="01" title="Nearest in the registry" id="nearest">
            The closest constellations on record, for the record. None comes close.
          </SectionHead>
          <CandidateTable candidates={query.candidates} />
        </section>
      )}
    </>
  );
}

// ------------------------------------------------------------------ no shark / error

export function NoSignalResult({ query }: { query: Query }) {
  const failed = query.status === "error";
  return (
    <section className="in-wrap in-grid" style={{ paddingTop: 48, rowGap: 40 }}>
      <div style={{ gridColumn: "span 5", display: "grid", gap: 24, alignContent: "start" }}>
        <p className="label label-signal">
          {failed ? "Result · instrument fault" : "Result · no signal"}
        </p>
        <h1 className="display-2">{failed ? "The measurement failed." : "No readable pattern."}</h1>
        <p className="alert">
          {query.message ??
            (failed
              ? "The identification failed on our side. Try again in a moment."
              : "Not enough of a whale shark’s spots were found to fingerprint this photograph.")}
        </p>
        {!failed && (
          <div>
            <p className="label" style={{ marginBottom: 10 }}>
              For a readable photograph
            </p>
            <PhotoTips />
            <p style={{ marginTop: 12, fontSize: 14 }}>
              <Link to={`${to.about}#photographing`} className="link-u">
                Why the angle matters
              </Link>
            </p>
          </div>
        )}
        <Link to={to.home} className="btn" style={{ justifySelf: "start" }}>
          New measurement <span className="btn-arrow">→</span>
        </Link>
      </div>
      <figure style={{ gridColumn: "7 / span 6", margin: 0 }}>
        <div className="fig-head">
          <span className="label">A · Query · your photograph</span>
          <span className="label">0 spots</span>
        </div>
        <div className="plate">
          <img
            src={query.image_url}
            alt="Your photograph"
            style={{ width: "100%", display: "block", opacity: 0.85 }}
          />
        </div>
      </figure>
    </section>
  );
}
